import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  User, Bell, Shield, Palette, LogOut, ChevronRight,
  Mail, Lock, Trash2, Eye, EyeOff, Globe, Smartphone, Bug
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError, authAPI } from '../lib/api';
import usePageTitle from '../hooks/usePageTitle';
import { signInWithPopup } from 'firebase/auth';
import { auth as firebaseAuth, googleProvider } from '../lib/firebase';

const Settings = () => {
  usePageTitle('Settings');
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('account');
  const [saving, setSaving] = useState(false);

  // Notification preferences — hydrated from user record on mount.
  // Each email_* key matches a `email_pref_key` passed to backend notify_user().
  // Absent key on a user = opt-in (true) — only an explicit `false` suppresses email.
  const DEFAULT_NOTIF_PREFS = {
    email_messages: true,
    email_mentions: true,
    email_likes_comments: true,
    email_jobs: true,
    email_bookings: true,
    email_events: true,
    push_messages: true,
    push_matches: true,
    push_reminders: true,
    push_marketing: false,
  };
  const [notifications, setNotifications] = useState(DEFAULT_NOTIF_PREFS);

  // Privacy settings — hydrated from user record on mount.
  const DEFAULT_PRIVACY = {
    profile_visible: true,
    show_email: false,
    show_phone: false,
    show_location: true,
    allow_messages: true,
    show_in_dating: true,
  };
  const [privacy, setPrivacy] = useState(DEFAULT_PRIVACY);

  // Hydrate when the user object loads / changes (e.g. after login).
  useEffect(() => {
    if (user?.notification_preferences) {
      setNotifications({ ...DEFAULT_NOTIF_PREFS, ...user.notification_preferences });
    }
    if (user?.privacy_settings) {
      setPrivacy({ ...DEFAULT_PRIVACY, ...user.privacy_settings });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Email change
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);

  // Password change
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Google linking
  const [linkingGoogle, setLinkingGoogle] = useState(false);

  // Account deletion
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleChangeEmail = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setChangingEmail(true);
    try {
      await authAPI.changeEmail({ new_email: newEmail.trim(), current_password: emailPassword });
      toast.success('Email updated! Please log in again with your new email.');
      setShowEmailDialog(false);
      setNewEmail('');
      setEmailPassword('');
      setTimeout(() => { logout(); navigate('/login'); }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally { setChangingEmail(false); }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setChangingPassword(true);
    try {
      await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password updated successfully');
      setShowPasswordDialog(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally { setChangingPassword(false); }
  };

  const handleLinkGoogle = async () => {
    setLinkingGoogle(true);
    try {
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await result.user.getIdToken();
      await authAPI.linkGoogle({ google_id_token: idToken });
      toast.success('Google account linked! You can now sign in with Google.');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to link Google account');
    } finally { setLinkingGoogle(false); }
  };

  const handleUnlinkGoogle = async () => {
    if (!window.confirm('Unlink Google? You must have a password set to keep access to your account.')) return;
    try {
      await authAPI.unlinkGoogle();
      toast.success('Google account unlinked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink Google');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await authAPI.deleteAccount();
      setShowDeleteDialog(false);
      toast.success('Your account has been deleted.');
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(formatApiError(error) || 'Failed to delete account. Please contact support.');
      setDeleting(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await updateProfile({ notification_preferences: notifications });
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrivacy = async () => {
    setSaving(true);
    try {
      await updateProfile({ privacy_settings: privacy });
      toast.success('Privacy settings saved');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'account', icon: User, label: 'Account' },
    { id: 'notifications', icon: Bell, label: 'Notifications' },
    { id: 'privacy', icon: Shield, label: 'Privacy' },
    { id: 'support', icon: Bug, label: 'Support' },
  ];

  const openCrispChat = () => {
    if (window.$crisp) {
      try { window.$crisp.push(['do', 'chat:open']); } catch (e) { toast.info('Support chat is loading...'); }
    } else {
      toast.info('Support chat is not available. Email support@gradlink.com instead.');
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="font-heading font-extrabold text-3xl text-dark-slate mb-6">Settings</h1>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Sidebar nav */}
          <div className="md:col-span-1">
            <nav className="space-y-1">
              {sections.map(section => (
                <button key={section.id} onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-colors ${
                    activeSection === section.id
                      ? 'bg-mint-fresh/30 text-dark-slate font-medium'
                      : 'text-slate-gray hover:bg-slate-50'
                  }`}>
                  <section.icon className="w-5 h-5" />
                  <span className="text-sm">{section.label}</span>
                </button>
              ))}
              <Separator className="my-3" />
              <button onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left text-red-600 hover:bg-red-50 transition-colors">
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Sign Out</span>
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="md:col-span-3">
            {/* Account */}
            {activeSection === 'account' && (
              <div className="space-y-6">
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                  <h2 className="font-heading font-bold text-xl text-dark-slate mb-4">Account Information</h2>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-slate-gray">Email</Label>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-slate-gray mr-2" />
                          <span className="text-dark-slate">{user?.email}</span>
                        </div>
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => { setNewEmail(''); setEmailPassword(''); setShowEmailDialog(true); }}>
                          Change
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-gray">Name</Label>
                      <p className="text-dark-slate mt-1">{user?.name}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-gray">Institution</Label>
                      <p className="text-dark-slate mt-1">{user?.institution || 'Not set'}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-gray">Member since</Label>
                      <p className="text-dark-slate mt-1">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : '—'}
                      </p>
                    </div>
                  </div>
                  <Separator className="my-5" />
                  <Button variant="outline" onClick={() => navigate('/profile')} className="rounded-full">
                    <User className="w-4 h-4 mr-2" />Edit Profile
                  </Button>
                </div>

                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                  <h2 className="font-heading font-bold text-xl text-dark-slate mb-4">Security & Sign-In</h2>
                  <div className="space-y-4">
                    {/* Password */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-dark-slate flex items-center"><Lock className="w-4 h-4 mr-2 text-slate-gray" />Password</p>
                        <p className="text-xs text-slate-gray mt-0.5">{user?.has_password !== false ? 'Sign in with email and password' : 'No password set'}</p>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => { setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setShowPasswordDialog(true); }}>
                        {user?.has_password !== false ? 'Change Password' : 'Set Password'}
                      </Button>
                    </div>
                    <Separator />
                    {/* Google Sign-In */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-dark-slate flex items-center gap-2">
                          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          Google Sign-In
                        </p>
                        <p className="text-xs text-slate-gray mt-0.5">
                          {user?.google_linked_email ? `Linked: ${user.google_linked_email}` : 'Not linked — link to sign in with Google'}
                        </p>
                      </div>
                      {user?.google_linked_email ? (
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={handleUnlinkGoogle}>
                          Unlink
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={handleLinkGoogle} disabled={linkingGoogle}>
                          {linkingGoogle ? 'Linking...' : 'Link Google'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 border border-red-100 shadow-ambient">
                  <h2 className="font-heading font-bold text-xl text-red-600 mb-2">Danger Zone</h2>
                  <p className="text-slate-gray text-sm mb-4">
                    Deleting your account permanently removes your profile, posts, messages,
                    clips, listings, and uploaded media. Payment records are retained for
                    accounting but de-linked from your identity. This cannot be undone.
                  </p>
                  <Button variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => { setDeleteConfirmText(''); setShowDeleteDialog(true); }}>
                    <Trash2 className="w-4 h-4 mr-2" />Delete Account
                  </Button>
                </div>
              </div>
            )}

            {/* Notifications */}
            {activeSection === 'notifications' && (
              <div className="space-y-6">
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                  <h2 className="font-heading font-bold text-xl text-dark-slate mb-5">Email Notifications</h2>
                  <div className="space-y-4">
                    {[
                      { key: 'email_messages', label: 'Direct messages', desc: 'When someone sends you a DM' },
                      { key: 'email_mentions', label: 'Mentions', desc: 'When someone @-mentions you in a post, comment, or DM' },
                      { key: 'email_likes_comments', label: 'Likes & comments', desc: 'When someone likes or comments on your posts' },
                      { key: 'email_jobs', label: 'Job activity', desc: 'Applications received on posted jobs, status updates on yours' },
                      { key: 'email_bookings', label: 'Bookings', desc: 'Booking requests and status changes' },
                      { key: 'email_events', label: 'Event reminders', desc: 'Upcoming events you RSVPd to' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2">
                        <div>
                          <p className="font-medium text-dark-slate text-sm">{item.label}</p>
                          <p className="text-xs text-slate-gray">{item.desc}</p>
                        </div>
                        <Switch checked={notifications[item.key]}
                          onCheckedChange={(v) => setNotifications(prev => ({ ...prev, [item.key]: v }))} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                  <h2 className="font-heading font-bold text-xl text-dark-slate mb-5">Push Notifications</h2>
                  <div className="space-y-4">
                    {[
                      { key: 'push_messages', label: 'Messages', desc: 'Instant message notifications' },
                      { key: 'push_matches', label: 'New matches', desc: 'Dating and mentor matches' },
                      { key: 'push_reminders', label: 'Reminders', desc: 'Booking and event reminders' },
                      { key: 'push_marketing', label: 'Updates & tips', desc: 'GradLink news and feature updates' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2">
                        <div>
                          <p className="font-medium text-dark-slate text-sm">{item.label}</p>
                          <p className="text-xs text-slate-gray">{item.desc}</p>
                        </div>
                        <Switch checked={notifications[item.key]}
                          onCheckedChange={(v) => setNotifications(prev => ({ ...prev, [item.key]: v }))} />
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={handleSaveNotifications} disabled={saving}
                  className="bg-dark-slate text-white rounded-full">
                  {saving ? 'Saving...' : 'Save Notification Preferences'}
                </Button>
              </div>
            )}

            {/* Privacy */}
            {activeSection === 'privacy' && (
              <div className="space-y-6">
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                  <h2 className="font-heading font-bold text-xl text-dark-slate mb-5">Profile Visibility</h2>
                  <div className="space-y-4">
                    {[
                      { key: 'profile_visible', label: 'Public profile', desc: 'Allow other alumni to find and view your profile', icon: Eye },
                      { key: 'show_email', label: 'Show email', desc: 'Display your email on your profile', icon: Mail },
                      { key: 'show_phone', label: 'Show phone', desc: 'Display your phone number on your profile', icon: Smartphone },
                      { key: 'show_location', label: 'Show location', desc: 'Display your current location', icon: Globe },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2">
                        <div className="flex items-center space-x-3">
                          <item.icon className="w-4 h-4 text-slate-gray" />
                          <div>
                            <p className="font-medium text-dark-slate text-sm">{item.label}</p>
                            <p className="text-xs text-slate-gray">{item.desc}</p>
                          </div>
                        </div>
                        <Switch checked={privacy[item.key]}
                          onCheckedChange={(v) => setPrivacy(prev => ({ ...prev, [item.key]: v }))} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                  <h2 className="font-heading font-bold text-xl text-dark-slate mb-5">Communication</h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-2">
                      <div>
                        <p className="font-medium text-dark-slate text-sm">Allow direct messages</p>
                        <p className="text-xs text-slate-gray">Let other alumni send you messages</p>
                      </div>
                      <Switch checked={privacy.allow_messages}
                        onCheckedChange={(v) => setPrivacy(prev => ({ ...prev, allow_messages: v }))} />
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <div>
                        <p className="font-medium text-dark-slate text-sm">Appear in dating</p>
                        <p className="text-xs text-slate-gray">Show your profile in the dating section</p>
                      </div>
                      <Switch checked={privacy.show_in_dating}
                        onCheckedChange={(v) => setPrivacy(prev => ({ ...prev, show_in_dating: v }))} />
                    </div>
                  </div>
                </div>

                <Button onClick={handleSavePrivacy} disabled={saving}
                  className="bg-dark-slate text-white rounded-full">
                  {saving ? 'Saving...' : 'Save Privacy Settings'}
                </Button>
              </div>
            )}

            {activeSection === 'support' && (
              <div className="space-y-6">
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                  <h2 className="font-heading font-bold text-xl text-dark-slate mb-5">Help & Support</h2>
                  <div className="space-y-4">
                    <Button onClick={openCrispChat} className="w-full justify-start bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl h-auto py-4">
                      <Bug className="w-5 h-5 mr-3" />
                      <div className="text-left">
                        <p className="font-medium">Report a Bug</p>
                        <p className="text-xs opacity-75">Open a chat with our support team</p>
                      </div>
                    </Button>
                    <Button variant="outline" onClick={openCrispChat} className="w-full justify-start rounded-xl h-auto py-4">
                      <Mail className="w-5 h-5 mr-3 text-slate-gray" />
                      <div className="text-left">
                        <p className="font-medium text-dark-slate">Contact Support</p>
                        <p className="text-xs text-slate-gray">Chat with us or email support@gradlink.com</p>
                      </div>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Change Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader><DialogTitle>Change Email Address</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Current Email</Label>
                <p className="text-sm text-slate-gray mt-1">{user?.email}</p>
              </div>
              <div>
                <Label>New Email *</Label>
                <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="kellye@gradlink.co" className="mt-1 rounded-xl" />
              </div>
              {user?.has_password !== false && (
                <div>
                  <Label>Current Password</Label>
                  <Input type="password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} placeholder="Enter your current password" className="mt-1 rounded-xl" />
                </div>
              )}
              <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
                ⚠️ After changing your email, you'll be signed out and need to log back in with your new email.
              </p>
              <div className="flex gap-3">
                <Button onClick={handleChangeEmail} disabled={changingEmail || !newEmail.trim()} className="flex-1 h-11 bg-dark-slate text-white rounded-full">
                  {changingEmail ? 'Updating...' : 'Update Email'}
                </Button>
                <Button variant="outline" onClick={() => setShowEmailDialog(false)} className="h-11 rounded-full px-6">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={(open) => { if (!deleting) setShowDeleteDialog(open); }}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader><DialogTitle className="text-red-600">Delete Account</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-gray">
                This will permanently delete your GradLink account and everything associated with it:
              </p>
              <ul className="text-sm text-slate-gray list-disc pl-5 space-y-1">
                <li>Your profile, posts, comments, and clips</li>
                <li>Direct messages you sent or received</li>
                <li>Listings, events, jobs, and dating profile</li>
                <li>Uploaded photos and videos</li>
                <li>Your Firebase Auth sign-in credentials</li>
              </ul>
              <p className="text-xs text-slate-gray">
                Payment records are retained for accounting/tax purposes but are de-linked from your identity.
                This action cannot be undone.
              </p>
              <div>
                <Label>Type <span className="font-mono text-red-600">DELETE</span> to confirm</Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="mt-1 rounded-xl"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteConfirmText !== 'DELETE'}
                  className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white rounded-full">
                  {deleting ? 'Deleting...' : 'Permanently Delete Account'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleting}
                  className="h-11 rounded-full px-6">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader><DialogTitle>{user?.has_password !== false ? 'Change Password' : 'Set Password'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              {user?.has_password !== false && (
                <div>
                  <Label>Current Password</Label>
                  <div className="relative mt-1">
                    <Input type={showCurrentPw ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="rounded-xl pr-10" />
                    <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              <div>
                <Label>New Password *</Label>
                <div className="relative mt-1">
                  <Input type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" className="rounded-xl pr-10" />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirm New Password *</Label>
                <Input type={showNewPw ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1 rounded-xl" />
              </div>
              <div className="flex gap-3">
                <Button onClick={handleChangePassword} disabled={changingPassword || !newPassword} className="flex-1 h-11 bg-dark-slate text-white rounded-full">
                  {changingPassword ? 'Updating...' : 'Update Password'}
                </Button>
                <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="h-11 rounded-full px-6">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Settings;
